import axios from 'axios'

// Token getter is injected by AuthProvider so this module has no direct
// dependency on the Auth0 SDK or React context.
let getAccessToken = null

export function setAccessTokenGetter(fn) {
  getAccessToken = fn
}

const client = axios.create({
  baseURL: '/api',
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// Request interceptor — attach auth token + log
client.interceptors.request.use(
  async (config) => {
    if (getAccessToken) {
      try {
        const token = await getAccessToken()
        if (token) config.headers.Authorization = `Bearer ${token}`
      } catch (err) {
        // Silent token failure — request goes out unauthenticated and the
        // backend's 401 will trigger the logout flow via the response interceptor.
        console.warn('[API] token fetch failed:', err?.message ?? err)
      }
    }
    config.metadata = { startTime: Date.now() }
    console.debug(`[API] → ${config.method?.toUpperCase()} ${config.url}`, {
      params: config.params,
    })
    return config
  },
  (error) => {
    console.error('[API] Request error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor — log duration and handle errors
client.interceptors.response.use(
  (response) => {
    const duration = Date.now() - (response.config.metadata?.startTime ?? Date.now())
    const serverMs = response.headers['x-process-time-ms']
    console.debug(
      `[API] ← ${response.status} ${response.config.url} (${duration}ms client` +
        (serverMs ? `, ${serverMs}ms server` : '') +
        ')'
    )
    return response
  },
  (error) => {
    const duration = Date.now() - (error.config?.metadata?.startTime ?? Date.now())
    if (error.response) {
      console.error(
        `[API] ← ${error.response.status} ${error.config?.url} (${duration}ms):`,
        error.response.data
      )
      if (error.response.status === 401) {
        // Token rejected by the backend — bounce the user through Auth0 again.
        window.dispatchEvent(new Event('auth:logout'))
      }
      // NOTE: a 403 is intentionally NOT treated as a global lockout. The only
      // 403s the backend emits are the per-router cerebi gates (/api/audit,
      // /api/admin) — expected denials for an allowed, authenticated customer
      // user who hits a cerebi-only sub-resource (e.g. the audit timeline
      // embedded in the action detail dialog). Dispatching `auth:forbidden`
      // here previously locked the entire app behind the "Access denied" screen
      // the moment such a user opened an action. The real not-on-the-allowlist
      // case is enforced at Auth0 login (api.access.deny → no token issued), so
      // a 403 on one sub-resource is a local concern: let the call reject and
      // the calling component render its own error/empty state.
    } else if (error.request) {
      console.error(`[API] No response for ${error.config?.url} (${duration}ms):`, error.message)
    } else {
      console.error('[API] Setup error:', error.message)
    }
    return Promise.reject(error)
  }
)

export default client
